import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck, ShieldOff, Copy, Check, X, AlertTriangle } from 'lucide-react';
import { auth } from '../api';

type Step = 'status' | 'scan' | 'codes' | 'disable';

/**
 * Enrolment and removal of TOTP two-factor authentication for the signed-in
 * staff account. Works with any RFC 6238 authenticator app.
 */
export default function TwoFactorModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [step, setStep] = useState<Step>('status');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const { data: status, isLoading } = useQuery({
    queryKey: ['2fa', 'status'],
    queryFn: auth.mfaStatus,
  });

  const { data: setup, mutate: startSetup, isPending: starting } = useMutation({
    mutationFn: auth.mfaSetup,
    onSuccess: () => {
      setError('');
      setStep('scan');
    },
    onError: handleError('Einrichtung konnte nicht gestartet werden.'),
  });

  const enableMutation = useMutation({
    mutationFn: () => auth.mfaEnable(code),
    onSuccess: ({ backupCodes }) => {
      setBackupCodes(backupCodes);
      setCode('');
      setError('');
      setStep('codes');
      qc.invalidateQueries({ queryKey: ['2fa', 'status'] });
    },
    onError: handleError('Code ungültig.'),
  });

  const disableMutation = useMutation({
    mutationFn: () => auth.mfaDisable(password, code),
    onSuccess: () => {
      setPassword('');
      setCode('');
      setError('');
      setStep('status');
      qc.invalidateQueries({ queryKey: ['2fa', 'status'] });
    },
    onError: handleError('Deaktivieren fehlgeschlagen.'),
  });

  function handleError(fallback: string) {
    return (err: unknown) => {
      setError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? fallback,
      );
    };
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-xl shadow-xl w-full max-w-md max-h-screen overflow-y-auto">
        <div className="px-6 py-5 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-base font-semibold text-slate-900">Zwei-Faktor-Authentifizierung</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
              {error}
            </div>
          )}

          {isLoading ? (
            <div className="h-20 bg-slate-100 rounded animate-pulse" />
          ) : step === 'status' ? (
            status?.enabled ? (
              <>
                <div className="flex items-start gap-3 bg-green-50 border border-green-200 rounded-lg px-4 py-3">
                  <ShieldCheck className="text-green-600 shrink-0 mt-0.5" size={20} />
                  <div className="text-sm">
                    <p className="font-medium text-green-900">Aktiv</p>
                    <p className="text-green-700 text-xs mt-0.5">
                      Seit {status.enabledAt ? new Date(status.enabledAt).toLocaleDateString('de-DE') : '—'}
                      {' · '}
                      {status.backupCodesRemaining} Wiederherstellungscode(s) übrig
                    </p>
                  </div>
                </div>
                {status.backupCodesRemaining <= 2 && (
                  <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800">
                    <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                    <span>
                      Nur noch wenige Wiederherstellungscodes. Deaktivieren und neu einrichten
                      erzeugt einen frischen Satz.
                    </span>
                  </div>
                )}
                <button
                  onClick={() => { setStep('disable'); setError(''); }}
                  className="w-full inline-flex items-center justify-center gap-1.5 border border-red-200 text-red-600 hover:bg-red-50 text-sm font-medium py-2.5 rounded-lg"
                >
                  <ShieldOff size={15} /> Deaktivieren
                </button>
              </>
            ) : (
              <>
                <p className="text-sm text-slate-600">
                  Schützen Sie Ihr Konto zusätzlich mit einem Einmalcode aus einer
                  Authenticator-App — etwa Google Authenticator, Microsoft Authenticator oder
                  1Password.
                </p>
                <button
                  onClick={() => startSetup()}
                  disabled={starting}
                  className="w-full bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium py-2.5 rounded-lg disabled:opacity-60"
                >
                  {starting ? 'Einen Moment…' : 'Einrichten'}
                </button>
              </>
            )
          ) : step === 'scan' && setup ? (
            <>
              <p className="text-sm text-slate-600">
                Scannen Sie diesen Code mit Ihrer Authenticator-App:
              </p>
              <div className="flex justify-center">
                <img
                  src={setup.qrDataUrl}
                  alt={t('auth.qrForSetup')}
                  className="border border-slate-200 rounded-lg"
                  width={200}
                  height={200}
                />
              </div>
              <details className="text-xs text-slate-500">
                <summary className="cursor-pointer">{t('auth.noScanner')}</summary>
                <code className="block mt-2 bg-slate-100 px-2 py-1.5 rounded font-mono break-all text-slate-700">
                  {setup.secret}
                </code>
              </details>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Code aus der App
                </label>
                <input
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  inputMode="numeric"
                  placeholder="123456"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-center text-lg tracking-[0.3em] font-mono focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
              <button
                onClick={() => enableMutation.mutate()}
                disabled={enableMutation.isPending || code.length < 6}
                className="w-full bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium py-2.5 rounded-lg disabled:opacity-60"
              >
                {enableMutation.isPending ? 'Wird geprüft…' : 'Aktivieren'}
              </button>
            </>
          ) : step === 'codes' ? (
            <>
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 text-xs text-amber-900">
                <AlertTriangle size={15} className="shrink-0 mt-0.5" />
                <span>
                  Bewahren Sie diese Wiederherstellungscodes sicher auf. Jeder ist einmal
                  verwendbar und sie werden <strong>nur jetzt</strong> angezeigt.
                </span>
              </div>
              <div className="grid grid-cols-2 gap-1.5 bg-slate-50 border border-slate-200 rounded-lg p-3">
                {backupCodes.map((c) => (
                  <code key={c} className="font-mono text-sm text-slate-800 text-center py-1">
                    {c}
                  </code>
                ))}
              </div>
              <button
                onClick={() => {
                  void navigator.clipboard.writeText(backupCodes.join('\n'));
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1800);
                }}
                className="w-full inline-flex items-center justify-center gap-1.5 border border-slate-300 text-slate-600 hover:bg-slate-50 text-sm font-medium py-2 rounded-lg"
              >
                {copied ? <Check size={14} /> : <Copy size={14} />} Codes kopieren
              </button>
              <button
                onClick={() => setStep('status')}
                className="w-full bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium py-2.5 rounded-lg"
              >
                Habe ich gesichert
              </button>
            </>
          ) : step === 'disable' ? (
            <>
              <p className="text-sm text-slate-600">
                Zum Deaktivieren bestätigen Sie bitte mit Passwort <em>und</em> einem gültigen
                Code — so kann eine übernommene Sitzung den Schutz nicht allein entfernen.
              </p>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Passwort</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Code aus der App
                </label>
                <input
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  inputMode="numeric"
                  placeholder="123456"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-center text-lg tracking-[0.3em] font-mono focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => { setStep('status'); setError(''); }}
                  className="flex-1 border border-slate-300 text-slate-600 text-sm font-medium py-2.5 rounded-lg"
                >
                  Abbrechen
                </button>
                <button
                  onClick={() => disableMutation.mutate()}
                  disabled={disableMutation.isPending || !password || code.length < 6}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white text-sm font-medium py-2.5 rounded-lg disabled:opacity-60"
                >
                  {disableMutation.isPending ? 'Einen Moment…' : 'Deaktivieren'}
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
